import { useEffect, useRef, useState, type ReactNode } from "react";
import { Camera, Check } from "lucide-react";
import { Field, Icon, Spinner } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useCapability } from "../../app/bandContext.js";
import { useAddMyExpense } from "../../app/hooks/useShows.js";
import {
  useFinalizeSettlement,
  useReopenSettlement,
  useSettlement,
  useShowTotalBreakdown,
  useUpdateSettlement,
} from "../../app/hooks/useMoney.js";
import { useUploadMedia } from "../../app/hooks/useMedia.js";
import { pushToast } from "../../app/toasts.js";
import { expenseLines } from "./lib/calendarMoney.js";
import styles from "./CalendarEventDetails.module.scss";

type Detail = components["schemas"]["OpsCalendarItemDetail"];
type SettlementDto = components["schemas"]["SettlementDto"];
type SettlementRead = components["schemas"]["SettlementRead"];
type UpdateSettlementRequest = components["schemas"]["UpdateSettlementRequest"];

/** The "Add expense" chips, in the handoff's order. The value is what the server stores. */
const CATEGORIES = [
  { value: "fuel", label: "Fuel" },
  { value: "food", label: "Food" },
  { value: "hotel", label: "Hotel" },
  { value: "gear", label: "Gear" },
  { value: "parking", label: "Parking" },
  { value: "other", label: "Other" },
] as const;

/** "Neptune Theatre · Aug 16" — how both sub-views name the show under their heading. */
function venueLine(detail: Detail): string {
  const day = new Date(`${(detail.date ?? "").slice(0, 10)}T12:00:00`)
    .toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return [detail.venueName || detail.title, day].filter(Boolean).join(" · ");
}

function money(cents: number | null | undefined): string {
  return ((cents ?? 0) / 100).toLocaleString(undefined, { style: "currency", currency: "USD" });
}

/** Dollars typed into the amount field → cents; null when it is not a positive amount. */
function toCents(text: string): number | null {
  const n = Number(text.replace(/[$,\s]/g, ""));
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : null;
}

/**
 * ART-305 v2 — "Add expense", inside the event's sheet / pop-up. An amount, one category chip, an optional "What
 * was it for" (the category names the line otherwise) and an optional receipt photo. It lands on the show's own
 * Expenses (the Booking page's list), stamped with who added it and owed back, so it also shows in Outstanding
 * Payments. Open to anyone going to the show; the server says whether they are.
 */
export function AddExpenseView({ detail, onDone, footer }: {
  detail: Detail;
  /** Back to where it was opened from (Details or Settlement), after a save or a Cancel. */
  onDone: () => void;
  /** Renders the sticky footer — the caller owns the actions frame so both sub-views sit on the same chrome. */
  footer: (node: ReactNode) => ReactNode;
}) {
  const add = useAddMyExpense();
  const { uploadMedia } = useUploadMedia();
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [receipt, setReceipt] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const cents = toCents(amount);
  const ready = cents != null && category != null;
  const busy = uploading || add.isPending;
  const venue = detail.venueName || detail.title || "the show";

  async function submit() {
    if (!ready || !detail.showId) return;
    let receiptMediaId: string | null = null;
    if (receipt) {
      setUploading(true);
      try {
        const media = await uploadMedia(receipt, {
          kind: receipt.type === "application/pdf" ? "pdf" : "image",
          purpose: "expense-receipt",
        });
        receiptMediaId = media.mediaId ?? null;
      } catch {
        pushToast("Couldn't upload the receipt. Try again, or add the expense without it.", "error");
        return;
      } finally {
        setUploading(false);
      }
    }
    add.mutate(
      { showId: detail.showId, body: { amountCents: cents, category, label: label.trim() || null, receiptMediaId } },
      {
        onSuccess: () => {
          pushToast(`Added to ${venue} expenses on the Booking page.`, "success");
          onDone();
        },
      },
    );
  }

  return (
    <>
      <div className={styles.scroll}>
        <div className={styles.subHead}>
          <h2 className={styles.title}>Add expense</h2>
          <p className={styles.date}>{venueLine(detail)}</p>
        </div>
        <div className={styles.form}>
          <label className={styles.amountBox}>
            <span className={styles.amountSign} aria-hidden>$</span>
            <input
              className={styles.amountInput}
              aria-label="Amount"
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              autoComplete="off"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </label>

          <p className={styles.formLabel} id="expense-category">Category</p>
          <div className={styles.chips} role="radiogroup" aria-labelledby="expense-category">
            {CATEGORIES.map((c) => (
              <button
                key={c.value}
                type="button"
                role="radio"
                aria-checked={category === c.value}
                className={styles.catChip}
                data-on={category === c.value || undefined}
                onClick={() => setCategory(c.value)}
              >
                {c.label}
              </button>
            ))}
          </div>

          <Field
            label="What was it for"
            placeholder="Strings and drum heads"
            value={label}
            maxLength={200}
            onChange={(e) => setLabel(e.target.value)}
          />

          <input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              e.target.value = "";
              if (f) setReceipt(f);
            }}
          />
          <button
            type="button"
            className={styles.receipt}
            data-attached={receipt ? "" : undefined}
            onClick={() => fileRef.current?.click()}
          >
            <Icon icon={receipt ? Check : Camera} size={20} />
            {receipt ? `${receipt.name || "Receipt"} attached` : "Add receipt photo"}
          </button>
          <p className={styles.helper}>Shows up in this show&apos;s expenses on the Booking page.</p>
        </div>
      </div>
      {footer(
        <div className={styles.buttons}>
          <button type="button" className={styles.btnGhost} onClick={onDone} disabled={busy}>Cancel</button>
          <button type="button" className={styles.btnPrimary} onClick={() => void submit()} disabled={!ready || busy}>
            {busy ? <Spinner size={16} /> : "Add expense"}
          </button>
        </div>,
      )}
    </>
  );
}

const SAVE_DEBOUNCE_MS = 600;

/**
 * ART-305 v2 — the show's Settlement, inside the event's sheet / pop-up, for the people who hold it on their phone
 * (`money:show_settlement`: the AV tier and the tour manager). It reads and writes the SAME settlement as the
 * Booking page and the Day of Show card, and every figure is the server's: the terms (the deal take — the higher of
 * the guarantee and the door share), merch, every expense line with who added it, and the one net to artist. Only
 * the two door counts are typed here (autosaved, the DOS card's rule); the deal is edited in the show editor.
 */
export function SettlementView({ detail, onAddExpense, footer }: {
  detail: Detail;
  onAddExpense: (() => void) | null;
  footer: (node: ReactNode) => ReactNode;
}) {
  const showId = detail.showId ?? undefined;
  const query = useSettlement(showId, true);
  const breakdown = useShowTotalBreakdown(showId, true);
  const update = useUpdateSettlement();
  const finalize = useFinalizeSettlement();
  const reopen = useReopenSettlement();
  const canFull = useCapability("money:full");

  const [edits, setEdits] = useState<{ presale: string; walkups: string } | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const readRef = useRef<SettlementRead | undefined>(undefined);
  readRef.current = query.data;

  // Autosave the door counts — a full-replace PUT, so every other stored field rides forward untouched.
  useEffect(() => {
    if (edits === null || !showId) return;
    const t = setTimeout(() => {
      const cur = readRef.current;
      if (!cur?.data) return;
      update.mutate({ showId, version: cur.version ?? 0, body: bodyFrom(cur.data, edits) });
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edits, showId]);

  const d = query.data?.data;
  if (query.isLoading) {
    return <div className={styles.loading} role="status" aria-live="polite"><Spinner /></div>;
  }
  if (query.isError || !d || !showId) {
    return (
      <div className={styles.scroll}>
        <div className={styles.subHead}><h2 className={styles.title}>Settlement</h2></div>
        <p className={styles.error}>Couldn&apos;t load the settlement.</p>
      </div>
    );
  }

  const settled = d.status === "settled";
  const e = edits ?? { presale: intText(d.presaleSold), walkups: intText(d.walkupSold) };
  const sold = (toInt(e.presale) ?? 0) + (toInt(e.walkups) ?? 0);
  const doorGross =
    (toInt(e.presale) ?? 0) * (d.presalePriceCents ?? 0) + (toInt(e.walkups) ?? 0) * (d.walkupPriceCents ?? 0)
    + (d.vipCount ?? 0) * (d.vipPriceCents ?? 0);
  const lines = expenseLines(breakdown.data?.expenses);
  const expensesTotal = (d.expensesTotalCents ?? 0) + (d.merchCutOwedCents ?? 0);
  const settledBy = detail.people?.find((p) => p.personId === d.settledByPersonId)?.name;
  const settledOn = d.settledOn
    ? new Date(d.settledOn).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : null;

  async function markSettled() {
    if (!showId) return;
    const cur = readRef.current;
    // A worksheet nobody has opened has no row yet, and settling needs one: write it first, then settle.
    if (cur?.data && (cur.version ?? 0) === 0) {
      await update.mutateAsync({ showId, version: 0, body: bodyFrom(cur.data, e) });
    }
    finalize.mutate(showId, {
      onSuccess: () => pushToast("Settlement saved. The Booking page is updated.", "success"),
    });
  }

  return (
    <>
      <div className={styles.scroll}>
        <div className={styles.subHead}>
          <div className={styles.subTitleRow}>
            <h2 className={styles.title}>Settlement</h2>
            <span className={styles.pill} data-tone={settled ? "going" : "invited"}>{settled ? "Settled" : "Open"}</span>
          </div>
          <p className={styles.date}>{venueLine(detail)}</p>
        </div>

        <div className={styles.form}>
          <p className={styles.formLabel}>Ticketing</p>
          <div className={styles.ledger}>
            <LedgerRow label="Presale sold">
              {settled ? (
                <span>{intText(d.presaleSold) || "0"}</span>
              ) : (
                <input className={styles.ledgerInput} aria-label="Presale sold" type="number" inputMode="numeric"
                  min="0" step="1" placeholder="0" value={e.presale}
                  onChange={(ev) => setEdits({ ...e, presale: ev.target.value })} />
              )}
            </LedgerRow>
            <LedgerRow label="Walk-ups">
              {settled ? (
                <span>{intText(d.walkupSold) || "0"}</span>
              ) : (
                <input className={styles.ledgerInput} aria-label="Walk-ups" type="number" inputMode="numeric"
                  min="0" step="1" placeholder="0" value={e.walkups}
                  onChange={(ev) => setEdits({ ...e, walkups: ev.target.value })} />
              )}
            </LedgerRow>
            <LedgerRow label="Ticket price">{priceText(d)}</LedgerRow>
            <LedgerRow label="Gross door" strong>{money(doorGross)}{sold > 0 ? ` · ${sold} sold` : ""}</LedgerRow>
          </div>

          <p className={styles.formLabel}>Income</p>
          <div className={styles.ledger}>
            <LedgerRow label="Terms" sub={termsText(d)}>{money(d.headlineTakeCents)}</LedgerRow>
            {(d.merchGrossCents ?? 0) > 0 && <LedgerRow label="Merch">{money(d.merchGrossCents)}</LedgerRow>}
          </div>

          <div className={styles.formLabelRow}>
            <p className={styles.formLabel}>Expenses</p>
            {!settled && onAddExpense && (
              <button type="button" className={styles.inlineLink} onClick={onAddExpense}>+ Add expense</button>
            )}
          </div>
          <div className={styles.ledger}>
            {(d.merchCutOwedCents ?? 0) > 0 && (
              <LedgerRow label="Merch cut" negative>{`−${money(d.merchCutOwedCents)}`}</LedgerRow>
            )}
            {lines.map((l, i) => (
              <LedgerRow key={`${l.label}-${i}`} label={l.label ?? "Expense"} negative
                sub={l.addedByName ? `${l.addedByName}${l.receiptMediaId ? " · receipt" : ""}` : undefined}>
                {`−${money(l.amountCents)}`}
              </LedgerRow>
            ))}
            {breakdown.isLoading && <div className={styles.ledgerRow}><Spinner size={14} /></div>}
            <LedgerRow label="Total expenses" strong negative total>{`−${money(expensesTotal)}`}</LedgerRow>
          </div>

          <div className={styles.netBlock}>
            <span>Net to artist</span>
            <span className={styles.netValue}>{money(d.netToArtistCents)}</span>
          </div>
          {settled && (
            <p className={styles.settledLine}>
              {["Settled", settledBy ? `by ${settledBy}` : null].filter(Boolean).join(" ")}
              {settledOn ? ` · ${settledOn}` : ""}
            </p>
          )}
          {settled && reason !== null && (
            <Field label="Reason for reopening" placeholder="Why is this being reopened?" value={reason}
              onChange={(ev) => setReason(ev.target.value)} />
          )}
        </div>
      </div>
      {footer(
        !settled ? (
          <div className={styles.buttons}>
            <button type="button" className={styles.btnPrimary} onClick={() => void markSettled()}
              disabled={finalize.isPending || update.isPending}>
              Mark as settled
            </button>
          </div>
        ) : canFull ? (
          reason === null ? (
            <div className={styles.buttons}>
              <button type="button" className={styles.btnGhost} onClick={() => setReason("")}>Reopen settlement</button>
            </div>
          ) : (
            <div className={styles.buttons}>
              <button type="button" className={styles.btnGhost} onClick={() => setReason(null)}>Cancel</button>
              <button type="button" className={styles.btnPrimary} disabled={reason.trim() === "" || reopen.isPending}
                onClick={() => reopen.mutate({ showId, body: { reason: reason.trim() } }, { onSuccess: () => setReason(null) })}>
                Reopen settlement
              </button>
            </div>
          )
        ) : null,
      )}
    </>
  );
}

function LedgerRow({ label, sub, strong, negative, total, children }: {
  label: string; sub?: string | null; strong?: boolean; negative?: boolean; total?: boolean; children: ReactNode;
}) {
  return (
    <div className={styles.ledgerRow} data-total={total || undefined}>
      <span className={styles.ledgerLabel}>
        {label}
        {sub && <span className={styles.ledgerSub}>{sub}</span>}
      </span>
      <span className={styles.ledgerValue} data-strong={strong || undefined} data-negative={negative || undefined}>
        {children}
      </span>
    </div>
  );
}

/** "$25 presale · $30 walk-up" — the stored prices; one when they agree. */
function priceText(d: SettlementDto): string {
  const pre = d.presalePriceCents ?? 0;
  const walk = d.walkupPriceCents ?? 0;
  if (pre === 0 && walk === 0) return "—";
  if (pre === walk || walk === 0) return money(pre);
  if (pre === 0) return money(walk);
  return `${money(pre)} presale · ${money(walk)} walk-up`;
}

/** "$1,000 guarantee vs 70% of door over $500" — the deal, as stored on the show. */
function termsText(d: SettlementDto): string | null {
  const pct = d.doorSplitPct ?? 0;
  const g = d.guaranteeCents ?? 0;
  const door = pct > 0
    ? `${pct}% of door${(d.doorSplitAfterCents ?? 0) > 0 ? ` over ${money(d.doorSplitAfterCents)}` : ""}`
    : null;
  if (g > 0 && door) return `${money(g)} guarantee vs ${door}`;
  if (door) return door;
  if (g > 0) return `${money(g)} guarantee`;
  return null;
}

function intText(n: number | null | undefined): string { return n == null ? "" : String(n); }
function toInt(s: string): number | undefined { const t = s.trim(); return t === "" ? undefined : Math.max(0, Math.round(Number(t))); }

/** The full-replace body: the two door counts from the sheet, everything else exactly as stored. */
function bodyFrom(dto: SettlementDto, e: { presale: string; walkups: string }): UpdateSettlementRequest {
  return {
    presaleSold: toInt(e.presale) ?? null,
    presalePriceCents: dto.presalePriceCents,
    walkupSold: toInt(e.walkups) ?? null,
    walkupPriceCents: dto.walkupPriceCents,
    vipCount: dto.vipCount,
    vipPriceCents: dto.vipPriceCents,
    merchGrossCents: dto.merchGrossCents,
    merchSoftGoodsPct: dto.merchSoftGoodsPct,
    merchRecordedPct: dto.merchRecordedPct,
    depositCents: dto.depositCents,
    depositReceivedOn: dto.depositReceivedOn,
  };
}
